import { AnswerSources, type SourceItem } from '@paladigm/answer-visuals';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { GAP_STATES, replyGaps, replyInfo } from '../health';
import type { StepHealth } from '../types';
import { useDocumentTheme, useVisualWidth } from '../visuals';

/**
 * Under an answer: the gaps in the data it rests on, the notes on what its figures cover (the
 * CRM's scope among them) and its Sources footer, from its steps' TASK_COMPLETED. A reply whose
 * frames carried none of these shows nothing.
 */
export function DataHealth({
  health,
  sources,
}: {
  health?: StepHealth[] | undefined;
  sources?: SourceItem[] | undefined;
}) {
  const { t } = useTranslation('ai-cio');
  const gaps = replyGaps(health, (state) => t(`health.state.${state}`, GAP_STATES[state] ?? ''));
  const info = replyInfo(health);
  if (gaps.length === 0 && info.length === 0 && !sources?.length) return null;
  return (
    <div className="mt-1 flex flex-col gap-2">
      {gaps.length > 0 && (
        <div role="note" className="rounded-md border border-warning/30 bg-warning/10 p-3">
          <div className="mb-1 flex items-center gap-1.5">
            <TriangleAlert className="size-3.5 shrink-0 text-warning" aria-hidden />
            <p className="text-xs font-medium text-warning">
              {t('health.title', 'Gaps in the data')}
            </p>
          </div>
          <ul className="space-y-0.5 text-xs leading-relaxed text-muted-foreground">
            {gaps.map((gap) => (
              <li key={`${gap.step ?? ''}\u0000${gap.text}`}>
                {gap.step && <span className="font-medium">{gap.step}: </span>}
                {gap.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      {info.length > 0 && (
        <div className="space-y-0.5 text-xs leading-relaxed text-muted-foreground">
          {info.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}
      {sources && sources.length > 0 && <SourcesFooter items={sources} />}
    </div>
  );
}

/** The package's Sources card, at the visuals' width, in the page's theme and language. */
function SourcesFooter({ items }: { items: SourceItem[] }) {
  const { i18n } = useTranslation();
  const width = useVisualWidth();
  const theme = useDocumentTheme();
  return <AnswerSources items={items} width={width} theme={theme} locale={i18n.language || 'en'} />;
}
